/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, OnInit, OnDestroy, Input, AfterViewInit, ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { ANIMATIONS, CLOSE_TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, take, tap } from 'rxjs/operators';
import * as urlTemplate from 'url-template';

import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { SubmitBookingArrangementProps } from '@booking/entities/booking-arrangement';
import { BtnLoaderSize } from '@ux/components/btn-loader/entities/btn-loader-styles';
import { loaderColors } from '@ux/entities/loader-colors';
import {
  AirValidationBySectorResponse,
  AirValidationFlightsSector,
  AirValidationResponse
} from '@app/entities/air-validation.response';
import { BookingFacade } from '@booking/booking.facade';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { AppFacade } from '@app/app.facade';
import { removeSubscriptions } from '@shared/helpers/shared-helpers';
import { autoApi } from '@autos/auto.api';
import { TIME_SHOW_CHECKOUT } from '@booking/constants/timer.constants';
import { AlertMessageType } from '@ux/components/alert-message/alert-message-type';
import { ActivitiesValidateResponse } from '@activities/entities/activities-booking';
import { 
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';

// Temporal logic until Netactica solution 
import { SessionStorageService } from '@app/services/session-storage.service';

@Component({
  selector: 'app-booking-waiting-page',
  templateUrl: './booking-waiting-page.container.html',
  styleUrls: ['./booking-waiting-page.container.sass'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BookingWaitingPageContainer implements OnInit, OnDestroy, AfterViewInit {

  @Input() props: SubmitBookingArrangementProps;
  @Input() type: TravelBookingType;
  @Input() isMobile: boolean;
  @Input() airValidation: AirValidationResponse;
  @Input() airValidationBySector: AirValidationBySectorResponse;
  @Input() activitiesValidation: ActivitiesValidateResponse;
  @Input() hotelValidation: HotelValidationResponse;
  @Input() autoValidation: VehicleValidateResponse;
  @Input() capacity: number;

  public animation = ANIMATIONS.SLIDE_IN_RIGHT;
  public btnSize = BtnLoaderSize.MD;
  public loaderColor = loaderColors.RED;
  public showProviderAlert = false;
  public alertType = AlertMessageType.WARNING;

  public readonly icon = Icon;
  public readonly iconColor = IconFillColor;
  public readonly iconSize = 20;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  public readonly CHUBB_LOGO: string = 'chubb-logo.png';
  public readonly CHUBB_BOOKING_IMAGES_BASE_PATH: string = 'web/travel/Chubb/Booking';

  private timeout: NodeJS.Timeout;

  protected providerToShowAlert = 'LatamNDC';
  protected timeToShowAlert = 10000;
  protected subscriptions: Subscription[] = [];
  protected timeShowCheckout = TIME_SHOW_CHECKOUT;
  protected closeTimeToSlide = CLOSE_TIME_TO_SLIDE;

  constructor(
    private readonly appFacade: AppFacade,
    private readonly router: Router,
    private readonly domManipulateService: DomManipulateService,
    private readonly facade: BookingFacade,
    private readonly cdr: ChangeDetectorRef,
    private readonly sessionStorage: SessionStorageService // Temporal logic until Netactica solution 
  ) {
    const extras = this.router.getCurrentNavigation()?.extras;
    if (extras && extras.state) {
      this.props = extras.state.props;
      this.type = extras.state.type;
      this.isMobile = !!extras.state.isMobile;
      this.airValidation = extras.state.airValidation;
      this.airValidationBySector = extras.state.airValidationBySector;
      this.hotelValidation = extras.state.hotelValidation;
      this.autoValidation = extras.state.autoValidation;
      this.activitiesValidation = extras.state.activitiesValidation;
      this.capacity = extras.state.capacity;
    }
  }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.domManipulateService.setBookingScroll(false);
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.domManipulateService.setBookingScroll(true);
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    removeSubscriptions(this.subscriptions);
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(this.closeWaitingPageByError$);
    this.subscriptions.push(this.closeWaitingPageBySuccess$);
    this.addProviderAlertMessage();
  }

  private get closeWaitingPageByError$(): Subscription {
    return combineLatest([this.facade.hasErrorInBooking$, this.facade.isChurningExceded$]).pipe(
      distinctUntilChanged(),
      filter(([hasErrorInBooking, isChurningExceded]) => this.isMobile && (hasErrorInBooking || isChurningExceded)),
      take(1),
      tap(() => {
        setTimeout(() => {
          this.appFacade.navigateToLastRoute();
        }, this.closeTimeToSlide);
      })
    ).subscribe()
  }

  private get closeWaitingPageBySuccess$(): Subscription {
    return this.facade.proceedToCheckout$.pipe(
      distinctUntilChanged(),
      filter(proceedToCheckout => this.isMobile && proceedToCheckout),
      take(1),
      tap(() => {
        this.facade.proceedToCheckout();
        setTimeout(() => {
          this.appFacade.navigateToLastRoute();
        }, this.timeShowCheckout);
      })
    ).subscribe()
  }

  get airlineOrCompany(): AirValidationFlightsSector {
    if (
      this.type === TravelBookingType.FLIGHTS ||
      this.type === TravelBookingType.PACKAGE_AIR ||
      this.type === TravelBookingType.BUSES
    ) {
      return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0];
    } else if (this.type === TravelBookingType.FLIGHTS_BY_SECTOR) {
      return this.airValidationBySector.AirValidateResponse?.[0]?.TripOption?.Sectors?.[0]?.Flights?.[0];
    } else {
      return null;
    }
  }

  get isMultipleCarriers(): boolean {
    if (this.type === TravelBookingType.FLIGHTS) {
      return this.airValidation?.TripOption?.Sectors?.some(sector => {
        const codes = sector?.Flights?.map(f => f?.AirlineCode) || [];
        return codes?.length > 1 && !codes.every((val, i, arr) => val === arr[0]);
      });
    } else if (this.type === TravelBookingType.FLIGHTS_BY_SECTOR) {
      return this.airValidationBySector?.AirValidateResponse?.length > 1;
    } else {
      return false;
    }
  }

  get arrowIcon(): string {
    if (this.type === TravelBookingType.FLIGHTS || this.type === TravelBookingType.PACKAGE_AIR) {
      return this.airValidation?.TripOption?.Sectors?.length > 1 ? this.icon.Roundtrip : this.icon.ArrowRight;
    } else if (this.type === TravelBookingType.FLIGHTS_BY_SECTOR) {
      return this.isMultipleCarriers || this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.length > 1 ?
        this.icon.Roundtrip : this.icon.ArrowRight
    } else {
      return '';
    }
  }

  get departure(): string {
    switch (this.type) {
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureAirport;
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureAirport;
      default:
        return '';
    }
  }

  get arrivalOrDestination(): string {
    switch (this.type) {
      case TravelBookingType.BUSES:
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        if (this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.length) {
          const endOfFlights = this.airValidation.TripOption.Sectors[0].Flights.length - 1;
          return this.airValidation.TripOption.Sectors[0].Flights[endOfFlights].ArrivalAirport;
        } else {
          return '';
        }
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        if (this.airValidationBySector?.AirValidateResponse?.[0]?.TripOption?.Sectors?.[0]?.Flights?.length) {
          const endOfBySector = this.airValidationBySector.AirValidateResponse[0].TripOption.Sectors[0].Flights.length - 1;
          return this.airValidationBySector.AirValidateResponse[0].TripOption.Sectors[0].Flights[endOfBySector].ArrivalAirport;
        } else {
          return '';
        }
      case TravelBookingType.AUTOS:
        return this.autoValidation?.VehicleOption?.PickUp?.DestinationCode ?? '';
      case TravelBookingType.HOTELS:
        return this.hotelValidation?.HotelReservation?.Hotel?.CityCode ?? '';
      default:
        return '';
    }
  }

  get hotelName(): string {
    if (this.type === TravelBookingType.HOTELS || this.type === TravelBookingType.PACKAGE_AIR) {
      return this.hotelValidation?.HotelReservation?.Hotel?.HotelName ?? '';
    } else {
      return '';
    }
  }

  get autoModel(): string {
    if (this.type === TravelBookingType.AUTOS) {
      return this.autoValidation?.VehicleOption?.Vehicle?.Model ?? '';
    } else {
      return '';
    }
  }

  get autoCompany(): string {
    if (this.type === TravelBookingType.AUTOS) {
      return this.autoValidation?.VehicleOption?.Company?.Name ?? '';
    } else {
      return '';
    }
  }

  get autoCompanyImage(): string {
    if (this.type === TravelBookingType.AUTOS) {
      return this.autoValidation?.VehicleOption?.Company?.Code ? urlTemplate.parse(autoApi.auto_images).expand({
        provider: `${this.autoValidation.VehicleOption.Company.Code.toUpperCase()}`
      }) : '';
    } else {
      return '';
    }
  }

  get activityTitle(): string {
    return this.sessionStorage.getActivityName();
  }

  get activityName(): string {
    return this.activitiesValidation?.Products?.ProductsDetails?.[0].ProductName ?? '';
  }

  get orderWithAssistance(): boolean {
    return this.props?.assistance?.createIsEnabled;
  }

  get providerAlertMessage(): string {
    if (
      this.type === TravelBookingType.FLIGHTS ||
      this.type === TravelBookingType.PACKAGE_AIR ||
      this.type === TravelBookingType.FLIGHTS_BY_SECTOR
    ) {
      return this.addProviderAlertCountdownAndMessage ? 'BOOKING_MODULE.WAITING_PAGE.PROVIDER_ALERT.BY_AIRLINE' : '';
    } else {
      return '';
    }
  }

  get bookingType(): typeof TravelBookingType {
    return TravelBookingType;
  }

  private get providers(): string[] {
    switch (this.type) {
      case TravelBookingType.FLIGHTS:
      case TravelBookingType.PACKAGE_AIR:
        return [this.airValidation?.TripOption?.Provider];
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return this.airValidationBySector?.AirValidateResponse?.map(response => response?.TripOption?.Provider) ?? [];
      default:
        return [];
    }
  }

  private get addProviderAlertCountdownAndMessage(): boolean {
    if (
      this.type === TravelBookingType.FLIGHTS ||
      this.type === TravelBookingType.PACKAGE_AIR ||
      this.type === TravelBookingType.FLIGHTS_BY_SECTOR
    ) {
      return this.providers?.map(provider => provider?.toUpperCase())?.includes(this.providerToShowAlert?.toUpperCase());
    } else {
      return false;
    }
  }

  private addProviderAlertMessage(): void {
    if (this.addProviderAlertCountdownAndMessage) {
      this.timeout = setTimeout(() => {
        this.showProviderAlert = true;
        this.cdr.detectChanges();
      }, this.timeToShowAlert);
    }
  }
}
